import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Button, Divider, Form, Modal, Popover, Select, message } from 'antd';
import {
	DownOutlined,
	HolderOutlined,
	ExclamationCircleOutlined,
	PushpinFilled,
} from '@ant-design/icons';
import { nullSafeGet, stopDefaultEvents, getEntitiesById } from '../../utils/DataAccessUtils';
import {
	BASE_VIEW_OPTIONS,
	DEFAULT_WORK_ORDER_VIEW_ID,
	ROLE_TYPES,
	VIEW_ENTITY_TYPES,
} from '../../utils/DataConstants';
import { isWorkOrderLabelsTrackEnabled } from '../../data/workOrderStarLabels';
import { SavedFilterViewsPopoverRowDisplay } from '../saved_filter_views/SavedFilterViewsRowDisplay';
import SaveFiltersPopup from '../saved_filter_views/SaveFiltersPopup';
import { connect } from 'react-redux';
import { withUserType } from '../withUserType';
import {
	savedFilterViewsRestCrudThunksForBuyer,
	savedFilterViewsRestCrudThunksForSupplier,
} from '../../thunks/saved_filter_views_thunks';
import ReactDragListView from 'react-drag-listview';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';

import { getArchivedStatus } from '../../utils/WorkOrderUtils';
import { withCurrentUser } from '../../hooks/withCurrentUser';

require('./WorkOrdersOverviewPage.less');

export const WORK_ORDERS_SAVED_FILTER_VIEWS_TARGET_COLLECTION = 'workOrdersSavedViewsIndex';
export const WORK_ORDERS_SAVED_FILTER_PINNED_VIEWS_TARGET_COLLECTION =
	'workOrdersSavedFilterPinnedViews';
export const WORK_ORDERS_DEFAULT_VIEW_SELECT_TARGET_COLLECTION = 'workOrdersOverviewTabs';

interface WorkOrdersViewsPopoverProps {
	selectedViewId?: string;
	attentionDashboards: any;
	saveAttentionDashboard: (entity: any) => Promise<any>;
	onViewSelect: (view: any) => void;
	defaultView?: any;
	pinView: (entity: any) => Promise<any>;
	nonPinnedViewsData: any[];
	pinnedViewsData: any[];
	deleteView: (entity: any) => Promise<any>;
	updateView: (entity: any) => Promise<any>;
	fetchWorkOrderViews: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => Promise<any>;
	savedFilterViewsState: any;
	defaultViewId: number;
	userType: string;
	currentUser: any;
}

const WorkOrdersViewsPopover: React.FC<WorkOrdersViewsPopoverProps> = ({
	selectedViewId,
	attentionDashboards,
	saveAttentionDashboard,
	onViewSelect,
	defaultView,
	pinView,
	deleteView,
	nonPinnedViewsData,
	pinnedViewsData,
	updateView,
	fetchWorkOrderViews,
	savedFilterViewsState,
	defaultViewId,
	userType,
	currentUser,
}) => {
	const [viewsPopoverVisible, setViewsPopoverVisible] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editDefaultViewId, setEditDefaultViewId] = useState<string | undefined>(undefined);
	const [editPinnedViews, setEditPinnedViews] = useState<any[]>([]);
	const [editNonPinnedViews, setEditNonPinnedViews] = useState<any[]>([]);
	const [editingViewInModal, setEditingViewInModal] = useState<any | null>(null);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isUpdatingDefault, setIsUpdatingDefault] = useState(false);
	const [pinningViewIds, setPinningViewIds] = useState<Set<string>>(new Set());
	const [deletingViewId, setDeletingViewId] = useState<string | null>(null);

	const pinnedViewsDataRef = useRef(pinnedViewsData);
	pinnedViewsDataRef.current = pinnedViewsData;
	const nonPinnedViewsDataRef = useRef(nonPinnedViewsData);
	nonPinnedViewsDataRef.current = nonPinnedViewsData;

	const currentDefaultViewId = useMemo(() => {
		return (
			nullSafeGet('defaultWorkOrderFilterViewId', attentionDashboards) || DEFAULT_WORK_ORDER_VIEW_ID
		);
	}, [attentionDashboards]);

	const handleStartEdit = useCallback(() => {
		setViewsPopoverVisible(false);
		setIsEditing(true);
		setEditDefaultViewId(currentDefaultViewId ? currentDefaultViewId : undefined);
		setEditPinnedViews([...pinnedViewsData]);
		setEditNonPinnedViews([...nonPinnedViewsData]);
	}, [currentDefaultViewId, pinnedViewsData, nonPinnedViewsData]);

	const selectedViewOption = useMemo(() => {
		if (!selectedViewId) {
			return undefined;
		}
		return nonPinnedViewsData
			.filter((view) => !view.isPinned && `${nullSafeGet('id', view)}` !== `${defaultViewId}`)
			.find((view) => `${view.id}` === `${selectedViewId}`);
	}, [nonPinnedViewsData, selectedViewId, defaultViewId]);

	const hasSelectedView = !!selectedViewOption;

	const selectedViewLabel = useMemo(() => {
		return (selectedViewOption && selectedViewOption.name) || 'Views';
	}, [selectedViewOption]);

	const refetchViews = useCallback(() => {
		if (!fetchWorkOrderViews) return Promise.resolve();

		const fetchPinned = fetchWorkOrderViews(
			null,
			WORK_ORDERS_SAVED_FILTER_PINNED_VIEWS_TARGET_COLLECTION,
			{ current: 1, pageSize: 100 },
			{ sort_by: 'order', order: 'ascend' },
			{ entityType: VIEW_ENTITY_TYPES.WORKORDERS, isPinned: true },
			undefined
		).catch(() => {});

		const fetchNonPinned = fetchWorkOrderViews(
			null,
			WORK_ORDERS_SAVED_FILTER_VIEWS_TARGET_COLLECTION,
			{ current: 1, pageSize: 100 },
			{ sort_by: 'order', order: 'ascend' },
			{ entityType: VIEW_ENTITY_TYPES.WORKORDERS, isPinned: false },
			undefined
		).catch(() => {});

		return Promise.all([fetchPinned, fetchNonPinned]).then(() => undefined);
	}, [fetchWorkOrderViews]);

	const handlePopoverDefaultViewChange = useCallback(
		(value) => {
			const viewId = value && value.key ? value.key : value;
			const hideLoading = message.loading('Updating default view...', 0);
			setIsUpdatingDefault(true);

			const saveDashboard = saveAttentionDashboard
				? saveAttentionDashboard({
						...attentionDashboards,
						defaultWorkOrderFilterViewId: viewId || undefined,
				  })
				: Promise.resolve();

			// Save dashboard first, then pin the view only if dashboard save succeeded
			saveDashboard
				.then(() => {
					if (viewId) {
						const allViews = [...pinnedViewsData, ...nonPinnedViewsData];
						const newDefaultView = allViews.find((v) => `${v.id}` === `${viewId}`);
						if (newDefaultView && !newDefaultView.isPinned) {
							return updateView({ ...newDefaultView, isPinned: true });
						}
					}
				})
				.then(() => refetchViews())
				.then(() => {
					hideLoading();
					setIsUpdatingDefault(false);
					message.success('Default view updated');
				})
				.catch(() => {
					hideLoading();
					setIsUpdatingDefault(false);
					message.error('Failed to update default view');
				});
		},
		[
			saveAttentionDashboard,
			attentionDashboards,
			pinnedViewsData,
			nonPinnedViewsData,
			updateView,
			refetchViews,
		]
	);

	// Fetch non-pinned views when popover is visible
	useEffect(() => {
		if (viewsPopoverVisible && fetchWorkOrderViews) {
			fetchWorkOrderViews(
				null,
				WORK_ORDERS_SAVED_FILTER_VIEWS_TARGET_COLLECTION,
				{ current: 1, pageSize: 100 },
				{ sort_by: 'order', order: 'ascend' },
				{ entityType: VIEW_ENTITY_TYPES.WORKORDERS, isPinned: false },
				undefined
			).catch(() => {});
		}
	}, [viewsPopoverVisible, fetchWorkOrderViews]);

	const handleDragEnd = useCallback(
		(fromIndex: number, toIndex: number, isPinned: boolean) => {
			const reorder = (list: any[], startIndex: number, endIndex: number) => {
				const result = Array.from(list);
				const [removed] = result.splice(startIndex, 1);
				result.splice(endIndex, 0, removed);
				return result;
			};

			if (!isEditing) {
				const data = isPinned ? pinnedViewsData : nonPinnedViewsData;
				if (!data || data.length === 0) return;

				const newOrder = reorder(data, fromIndex, toIndex);
				const updatedViews = newOrder.map((view: any, index: number) => ({
					...view,
					order: index + 1,
				}));

				const hideLoading = message.loading('Reordering views...', 0);

				const originalOrderMap = new Map(data.map((v: any) => [v.id, v.order]));
				const changedViews = updatedViews.filter(
					(v: any) => v.order !== originalOrderMap.get(v.id)
				);

				Promise.all(changedViews.map((view: any) => updateView(view)))
					.then(() => {
						return refetchViews();
					})
					.then(() => {
						hideLoading();
						message.success('Views reordered successfully');
					})
					.catch(() => {
						hideLoading();
						message.error('Failed to reorder views');
					});
			} else {
				// Edit mode: update local state
				const data = isPinned ? editPinnedViews : editNonPinnedViews;
				if (!data || data.length === 0) return;

				const newOrder = reorder(data, fromIndex, toIndex);
				const updatedViews = newOrder.map((view: any, index: number) => ({
					...view,
					order: index + 1,
				}));

				if (isPinned) {
					setEditPinnedViews(updatedViews);
				} else {
					setEditNonPinnedViews(updatedViews);
				}
			}
		},
		[
			isEditing,
			nonPinnedViewsData,
			pinnedViewsData,
			editPinnedViews,
			editNonPinnedViews,
			updateView,
			refetchViews,
		]
	);

	const handleCancelEdit = useCallback(() => {
		setIsEditing(false);
		setEditDefaultViewId(undefined);
		setEditPinnedViews([]);
		setEditNonPinnedViews([]);
	}, []);

	const handleUpdateEdit = useCallback(() => {
		setIsUpdating(true);
		const hideLoading = message.loading('Updating views...', 0);

		// Step 1: reordering updates (must complete before pinning/default changes)
		// Only update views whose order actually changed
		const pinnedOrderMap = new Map(pinnedViewsData.map((v) => [v.id, v.order]));
		const nonPinnedOrderMap = new Map(nonPinnedViewsData.map((v) => [v.id, v.order]));

		const changedPinned = editPinnedViews.filter((v) => v.order !== pinnedOrderMap.get(v.id));
		const changedNonPinned = editNonPinnedViews.filter(
			(v) => v.order !== nonPinnedOrderMap.get(v.id)
		);

		const reorderPromises = [...changedPinned, ...changedNonPinned].map((view) => updateView(view));

		Promise.all(reorderPromises)
			.then(() => {
				// Step 2: saveAttentionDashboard + pin default view run after reordering
				// so that a reorder cannot overwrite the pin state set here
				const prevDefaultViewId = currentDefaultViewId ? `${currentDefaultViewId}` : undefined;
				if (`${editDefaultViewId}` === `${prevDefaultViewId}`) {
					return Promise.resolve();
				}

				// Save dashboard first, then pin the view only if dashboard save succeeded
				const saveDashboard = saveAttentionDashboard
					? saveAttentionDashboard({
							...attentionDashboards,
							defaultWorkOrderFilterViewId: editDefaultViewId,
					  })
					: Promise.resolve();

				return saveDashboard.then(() => {
					if (editDefaultViewId) {
						const allEditViews = [...editPinnedViews, ...editNonPinnedViews];
						const newDefaultView = allEditViews.find((v) => `${v.id}` === `${editDefaultViewId}`);
						if (newDefaultView && !newDefaultView.isPinned) {
							return updateView({ ...newDefaultView, isPinned: true });
						}
					}
				});
			})
			.then(() => {
				return refetchViews();
			})
			.then(() => {
				hideLoading();
				setIsUpdating(false);
				message.success('Views updated successfully');
				setIsEditing(false);
				setEditDefaultViewId(undefined);
				setEditPinnedViews([]);
				setEditNonPinnedViews([]);
			})
			.catch(() => {
				hideLoading();
				setIsUpdating(false);
				message.error('Failed to update views');
			});
	}, [
		editDefaultViewId,
		currentDefaultViewId,
		editPinnedViews,
		editNonPinnedViews,
		pinnedViewsData,
		nonPinnedViewsData,
		saveAttentionDashboard,
		attentionDashboards,
		updateView,
		refetchViews,
	]);

	const baseViewsForSelector = useMemo(() => {
		// The starred/"Tracked" base view is gated behind workorder-labels-track.
		const labelsTrackEnabled = isWorkOrderLabelsTrackEnabled();
		return BASE_VIEW_OPTIONS.filter((view) => labelsTrackEnabled || view.url !== 'starred').map(
			(view) => ({
				id: view.id,
				name: view.name,
				entityType: VIEW_ENTITY_TYPES.WORKORDERS,
				isPinned: true,
			})
		);
	}, []);

	const handleModalPinToggle = useCallback(
		(entity: any) => {
			const viewId = `${entity.id}`;
			setPinningViewIds((prev) => new Set(prev).add(viewId));
			return pinView(entity)
				.then(() => {
					refetchViews();
					if (entity.isPinned) {
						// Being pinned: was in non-pinned → move to pinned
						setEditNonPinnedViews((prev) => prev.filter((v) => v.id !== entity.id));
						setEditPinnedViews((prev) => [...prev, entity]);
					} else {
						// Being unpinned: was in pinned → move to non-pinned
						setEditPinnedViews((prev) => prev.filter((v) => v.id !== entity.id));
						setEditNonPinnedViews((prev) => [...prev, entity]);
					}
				})
				.catch(() => {
					message.error('Failed to update view');
				})
				.finally(() => {
					setPinningViewIds((prev) => {
						const next = new Set(prev);
						next.delete(viewId);
						return next;
					});
				});
		},
		[pinView, refetchViews]
	);

	const handleDeleteView = useCallback(
		(record: any) => {
			setDeletingViewId(`${record.id}`);
			const hideLoading = message.loading('Deleting view...', 0);
			deleteView({ id: record.id })
				.then(() => {
					if (currentDefaultViewId && `${currentDefaultViewId}` === `${record.id}`) {
						if (saveAttentionDashboard) {
							saveAttentionDashboard({
								...attentionDashboards,
								defaultWorkOrderFilterViewId: undefined,
							}).catch(() => {
								message.error('Failed to clear default view');
							});
						}
						if (isEditing) {
							setEditDefaultViewId(`${DEFAULT_WORK_ORDER_VIEW_ID}`);
						}
					}
					if (isEditing) {
						setEditPinnedViews((prev) => prev.filter((v) => v.id !== record.id));
						setEditNonPinnedViews((prev) => prev.filter((v) => v.id !== record.id));
					}
					return refetchViews();
				})
				.then(() => {
					message.success('View deleted successfully');
				})
				.catch(() => {
					message.error('Failed to delete view');
				})
				.finally(() => {
					hideLoading();
					setDeletingViewId(null);
				});
		},
		[
			deleteView,
			currentDefaultViewId,
			saveAttentionDashboard,
			attentionDashboards,
			isEditing,
			refetchViews,
		]
	);

	const handleEditDefaultViewChange = useCallback((value) => {
		const viewId = value && value.key ? value.key : value;
		setEditDefaultViewId(viewId);
	}, []);

	const handleEditModalCancel = useCallback(() => {
		setEditingViewInModal(null);
		refetchViews().then(() => {
			setEditPinnedViews([...pinnedViewsDataRef.current]);
			setEditNonPinnedViews([...nonPinnedViewsDataRef.current]);
		});
	}, [refetchViews]);

	const handleEditModalDelete = useCallback(() => {
		const deletedId = editingViewInModal && editingViewInModal.id;
		if (editDefaultViewId && `${editDefaultViewId}` === `${deletedId}`) {
			setEditDefaultViewId(`${DEFAULT_WORK_ORDER_VIEW_ID}`);
		}
		if (currentDefaultViewId && `${currentDefaultViewId}` === `${deletedId}`) {
			if (saveAttentionDashboard) {
				saveAttentionDashboard({
					...attentionDashboards,
					defaultWorkOrderFilterViewId: undefined,
				}).catch(() => {
					message.error('Failed to clear default view');
				});
			}
		}
		setEditPinnedViews((prev) => prev.filter((v) => v.id !== deletedId));
		setEditNonPinnedViews((prev) => prev.filter((v) => v.id !== deletedId));
	}, [
		editingViewInModal,
		editDefaultViewId,
		currentDefaultViewId,
		saveAttentionDashboard,
		attentionDashboards,
	]);

	const renderViewRow = useCallback(
		(
			view: any,
			draggableClass: string | null,
			handleClass: string | null,
			inModal: boolean = false
		) => {
			// Cast to any once to avoid per-prop TS errors from the withCurrentUser HOC wrapper
			const PopoverRow = SavedFilterViewsPopoverRowDisplay as any;
			const isDefaultView = currentDefaultViewId && `${currentDefaultViewId}` === `${view.id}`;

			const handleRowClick = inModal
				? undefined
				: () => {
						setViewsPopoverVisible(false);
						onViewSelect(view);
				  };

			const handleOpenView = (selectedView: any) => {
				setViewsPopoverVisible(false);
				onViewSelect(selectedView);
			};

			const handleSetDefault = () => {
				if (saveAttentionDashboard) {
					const newDefaultId = currentDefaultViewId === view.id ? undefined : view.id;
					saveAttentionDashboard({
						...attentionDashboards,
						defaultWorkOrderFilterViewId: newDefaultId,
					});
				}
			};

			return (
				<div
					key={view.id}
					className={`${
						draggableClass ? draggableClass + ' ' : ''
					}flex cursor-pointer flex-row items-center justify-between gap-3 px-4 py-2 hover:bg-gray-200`}
					onClick={handleRowClick}
				>
					<div className="flex flex-1 flex-row items-center gap-2">
						{draggableClass && (
							<HolderOutlined
								className={handleClass}
								style={{ cursor: 'move', color: '#8c8c8c' }}
								onClick={stopDefaultEvents}
							/>
						)}
						<PopoverRow
							record={view}
							isAdmin={false}
							onOpenView={handleOpenView}
							onDelete={handleDeleteView}
							allowSetDefault={true}
							setDefault={handleSetDefault}
							isDefault={isDefaultView}
							userType={userType}
							pinView={handleModalPinToggle}
							enableEdit={inModal}
							isDeleting={`${deletingViewId}` === `${view.id}`}
							isPinning={pinningViewIds.has(`${view.id}`)}
							onEdit={setEditingViewInModal}
						/>
					</div>
				</div>
			);
		},
		[
			currentDefaultViewId,
			onViewSelect,
			saveAttentionDashboard,
			attentionDashboards,
			handleModalPinToggle,
			handleDeleteView,
			userType,
			deletingViewId,
			pinningViewIds,
		]
	);

	const viewsPopoverContent = useMemo(() => {
		return (
			<div className="flex h-full max-h-96 flex-col">
				<div className="sticky top-0 z-10 bg-white">
					<div className="flex flex-row items-center justify-between gap-x-4 px-4 py-2 pb-3">
						<div className="h5medium text-character-primary">Views</div>
						<div className="flex flex-row items-center gap-2">
							<span className="text-character-tertiary text-sm" style={{ whiteSpace: 'nowrap' }}>
								Default:
							</span>
							<OWAsyncSelect
								style={{ width: 180, maxWidth: 400 }}
								size="small"
								loading={isUpdatingDefault}
								disabled={isUpdatingDefault}
								value={currentDefaultViewId}
								placeholder="Select default"
								onChange={handlePopoverDefaultViewChange}
								stateSlice={savedFilterViewsState}
								targetCollectionName={WORK_ORDERS_DEFAULT_VIEW_SELECT_TARGET_COLLECTION}
								fetchData={(
									searchText,
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								) => {
									return fetchWorkOrderViews(
										null,
										targetCollectionName,
										pagination || { current: 1, pageSize: 50 },
										sorting || { sort_by: 'name', order: 'ascend' },
										{ entityType: VIEW_ENTITY_TYPES.WORKORDERS, name: searchText },
										addToTargetCollection
									);
								}}
								fetchMultiple={() => {}}
								extraOptions={baseViewsForSelector}
								renderRecord={(view) => (
									<Select.Option key={view.id} value={view.id} label={view.name}>
										{view.name}
									</Select.Option>
								)}
								valueAccessor={(view) => view.id}
								labelAccessor={(view) => view.name}
								allowClear
								initialPagination={{ current: 1, pageSize: 50 }}
								sortBy={{ sort_by: 'name', order: 'ascend' }}
								additionalFilters={{ entityType: VIEW_ENTITY_TYPES.WORKORDERS }}
							/>
							<Button type="ghost" onClick={handleStartEdit}>
								Edit
							</Button>
						</div>
					</div>
					<Divider style={{ margin: 0 }} />
				</div>
				<div className="footnoteregular  rounded bg-grey-4 px-4 py-2 ">
					<PushpinFilled style={{ color: '#6027b0' }} /> Pinned views are displayed as tabs
				</div>
				<div className="flex-1 overflow-y-auto">
					{pinnedViewsData.length + nonPinnedViewsData.length < 1 ? (
						<div className="bodyregular flex flex-1 flex-row items-center justify-center gap-x-2 p-4 text-character-secondary">
							<ExclamationCircleOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />
							<span>No Saved views</span>
						</div>
					) : (
						<>
							{pinnedViewsData.length > 0 && (
								/* @ts-ignore - react-drag-listview type definitions are incomplete */
								<ReactDragListView
									nodeSelector=".popover-draggable-view-row-pinned"
									handleSelector=".popover-drag-handle-pinned"
									onDragEnd={(from: number, to: number) => handleDragEnd(from, to, true)}
								>
									{pinnedViewsData.map((view) =>
										renderViewRow(
											view,
											'popover-draggable-view-row-pinned',
											'popover-drag-handle-pinned'
										)
									)}
								</ReactDragListView>
							)}
							<Divider style={{ margin: 0 }} />
							{nonPinnedViewsData.length > 0 && (
								/* @ts-ignore - react-drag-listview type definitions are incomplete */
								<ReactDragListView
									nodeSelector=".popover-draggable-view-row"
									handleSelector=".popover-drag-handle"
									onDragEnd={(from: number, to: number) => handleDragEnd(from, to, false)}
								>
									{nonPinnedViewsData.map((view) =>
										renderViewRow(view, 'popover-draggable-view-row', 'popover-drag-handle')
									)}
								</ReactDragListView>
							)}
						</>
					)}
				</div>
			</div>
		);
	}, [
		handleStartEdit,
		pinnedViewsData,
		nonPinnedViewsData,
		renderViewRow,
		baseViewsForSelector,
		currentDefaultViewId,
		fetchWorkOrderViews,
		handlePopoverDefaultViewChange,
		savedFilterViewsState,
		handleDragEnd,
		isUpdatingDefault,
	]);

	const color = hasSelectedView ? 'text-character-primary' : 'text-white';
	return (
		<>
			<Popover
				trigger="click"
				visible={viewsPopoverVisible}
				overlayClassName={`viewsPopover min-w-96 px-0`}
				content={viewsPopoverContent}
				placement="bottomRight"
				onVisibleChange={setViewsPopoverVisible}
				overlayInnerStyle={{ padding: 0 }}
			>
				<Button
					type={hasSelectedView ? 'default' : 'ghost'}
					className={`mb-1 ml-4 ${color} text-transform-none`}
					style={{ padding: '2px 16px' }}
				>
					<span className={`mr-1 ${color} capitalize`}>{selectedViewLabel}</span>{' '}
					<DownOutlined style={{ color: hasSelectedView ? 'rgba(0,0,0,0.85)' : 'white' }} />
				</Button>
			</Popover>
			<Modal
				visible={isEditing}
				title="Edit Views"
				width={600}
				onCancel={handleCancelEdit}
				footer={[
					<Button key="cancel" onClick={handleCancelEdit} disabled={isUpdating}>
						Cancel
					</Button>,
					<Button key="update" type="primary" onClick={handleUpdateEdit} loading={isUpdating}>
						Update
					</Button>,
				]}
			>
				<Form.Item label="Default View" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
					<OWAsyncSelect
						style={{ width: '100%' }}
						value={editDefaultViewId}
						placeholder="Select a default view"
						onChange={handleEditDefaultViewChange}
						stateSlice={savedFilterViewsState}
						targetCollectionName={WORK_ORDERS_DEFAULT_VIEW_SELECT_TARGET_COLLECTION}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							return fetchWorkOrderViews(
								null,
								targetCollectionName,
								pagination || { current: 1, pageSize: 50 },
								sorting || { sort_by: 'name', order: 'ascend' },
								{ entityType: VIEW_ENTITY_TYPES.WORKORDERS, name: searchText },
								addToTargetCollection
							);
						}}
						fetchMultiple={() => {}}
						extraOptions={baseViewsForSelector}
						renderRecord={(view) => (
							<Select.Option key={view.id} value={view.id} label={view.name}>
								{getArchivedStatus(view.filterValue, currentUser)} / {view.name}
							</Select.Option>
						)}
						valueAccessor={(view) => view.id}
						labelAccessor={(view) => view.name}
						allowClear
						initialPagination={{ current: 1, pageSize: 50 }}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
						additionalFilters={{ entityType: VIEW_ENTITY_TYPES.WORKORDERS }}
					/>
				</Form.Item>

				<div className="bodymedium mb-4">Re-order Views</div>
				<div className="footnoteregular mb-3 flex flex-row items-center gap-x-2 rounded bg-grey-4 px-4 py-2 ">
					Order of <PushpinFilled style={{ color: '#6027b0' }} /> pinned views are reflected on tabs
				</div>
				<div className="max-h-72 overflow-y-auto">
					<div style={{ border: 'solid 1px rgba(240, 240 ,240,1)' }}>
						{editPinnedViews.length > 0 && (
							/* @ts-ignore - react-drag-listview type definitions are incomplete */
							<ReactDragListView
								nodeSelector=".modal-draggable-view-row-pinned"
								handleSelector=".modal-drag-handle-pinned"
								onDragEnd={(from: number, to: number) => handleDragEnd(from, to, true)}
							>
								{editPinnedViews.map((view) =>
									renderViewRow(
										view,
										'modal-draggable-view-row-pinned',
										'modal-drag-handle-pinned',
										true
									)
								)}
							</ReactDragListView>
						)}
					</div>

					<div style={{ marginTop: 16, border: 'solid 1px rgba(240, 240 ,240,1)' }}>
						{editNonPinnedViews.length > 0 && (
							/* @ts-ignore - react-drag-listview type definitions are incomplete */
							<ReactDragListView
								nodeSelector=".modal-draggable-view-row"
								handleSelector=".modal-drag-handle"
								onDragEnd={(from: number, to: number) => handleDragEnd(from, to, false)}
							>
								{editNonPinnedViews.map((view) =>
									renderViewRow(view, 'modal-draggable-view-row', 'modal-drag-handle', true)
								)}
							</ReactDragListView>
						)}
					</div>
				</div>
			</Modal>
			{editingViewInModal && (
				<SaveFiltersPopup
					viewId={`${editingViewInModal.id}`}
					filters={{}}
					preAppliedFilters={{}}
					roleType={userType}
					entityType={VIEW_ENTITY_TYPES.WORKORDERS}
					skipFilterUpdate={true}
					onCancel={handleEditModalCancel}
					onDelete={handleEditModalDelete}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state, ownProps) => {
	const collectionName = 'saved_filter_views';
	const relevantState = state[collectionName];

	let nonPinnedViewsData = [];
	let pinnedViewsData = [];

	if (relevantState) {
		if (relevantState[WORK_ORDERS_SAVED_FILTER_VIEWS_TARGET_COLLECTION]) {
			nonPinnedViewsData = getEntitiesById(
				relevantState[WORK_ORDERS_SAVED_FILTER_VIEWS_TARGET_COLLECTION].recordsIds || [],
				relevantState.records || []
			);
		}
		if (relevantState[WORK_ORDERS_SAVED_FILTER_PINNED_VIEWS_TARGET_COLLECTION]) {
			pinnedViewsData = getEntitiesById(
				relevantState[WORK_ORDERS_SAVED_FILTER_PINNED_VIEWS_TARGET_COLLECTION].recordsIds || [],
				relevantState.records || []
			);
		}
	}

	return {
		nonPinnedViewsData,
		pinnedViewsData,
		savedFilterViewsState: relevantState || {},
	};
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	pinView: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? savedFilterViewsRestCrudThunksForSupplier.update(entity)
				: savedFilterViewsRestCrudThunksForBuyer.update(entity)
		),
	deleteView: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? savedFilterViewsRestCrudThunksForSupplier.deleteImmediate(entity)
				: savedFilterViewsRestCrudThunksForBuyer.deleteImmediate(entity)
		),
	updateView: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? savedFilterViewsRestCrudThunksForSupplier.update(entity)
				: savedFilterViewsRestCrudThunksForBuyer.update(entity)
		),
});

// @ts-ignore - Type inference issue with HOC composition
export default withCurrentUser(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(WorkOrdersViewsPopover))
);
